import { swapDates } from "components/dashboard/product-template-table/utils/timeUtils";
import { useState } from "react";
import { addToDate, isSameEpochTime, JodaTimeUtils, LT_ZONE_ID, toInstant, toInstantOrNow } from "utils/time-utils";
import { timeConfigValidation } from "utils/utils";
import { TimeLoadConfig } from "models/dataset-models";
import { DateLoadConfig } from "api/model/dashboard/DashboardTab";
import { Instant, ZoneId } from "@js-joda/core";
import { useCompanyTimezone } from "../useTimezone";
import truncateToZoned = JodaTimeUtils.truncateToZoned;

interface TimeContext {
  referenceTime?: string;

  from?: string;
  to?: string;

  config?: any;
}

const useTimeState = (ctx: TimeContext) => {
  const companyTz = useCompanyTimezone();

  const getDefaultDate = (date: string | undefined) => {
    return date ? new Date(date) : undefined;
  };

  const [from, setFromState] = useState<Date | undefined>(getDefaultDate(ctx.from));
  const [to, setToState] = useState<Date | undefined>(getDefaultDate(ctx.to));

  const [config, setConfig] = useState<any | undefined>(ctx?.config);
  const [ctxInit] = useState<TimeContext | undefined>(ctx);

  const instantFrom = from ? toInstant(from) : undefined;
  const instantTo = to ? toInstant(to) : undefined;

  const applyReferenceTime = (time: Instant | undefined, cfg?: DateLoadConfig, forceLtTz?: boolean) => {
    if (ctxInit?.from && ctxInit.to) {
      return;
    }

    const instantTime = toInstantOrNow(time);
    applyTimeOptions(instantTime, cfg ?? config, forceLtTz);

    if (cfg) {
      setConfig(cfg);
    }
  };

  const applyTimeOptions = (refTime: Instant, cfg: TimeLoadConfig, forceLtTz?: boolean) => {
    const timezone = forceLtTz ? LT_ZONE_ID : companyTz;
    const { from: tFrom, to: tTo } = getTimeRange(refTime, cfg, timezone);
    if (!isSameEpochTime(tFrom, from) || !isSameEpochTime(tTo, to)) {
      setFrom(tFrom || from);
      setTo(tTo || to);
    }
  };

  const setFrom = (date: Date | string) => {
    const d = new Date(date);
    setFromState(d);
  };

  const setTo = (date: Date | string) => {
    const d = new Date(date);
    setToState(d);
  };

  return {
    applyReferenceTime: applyReferenceTime,
    from,
    instantFrom,
    setFrom,
    to,
    instantTo,
    setTo,
  };
};

const getTimeRange = (referenceTime: Instant, config: TimeLoadConfig, zoneId: ZoneId) => {
  const validatedConfig = timeConfigValidation(config || {});

  const tempDate = truncateToZoned(referenceTime, config?.alignStartTo, zoneId);

  const tempFrom = addToDate(tempDate, validatedConfig.startOffset!, validatedConfig.startOffsetUnits);
  const tempTo = addToDate(tempFrom, validatedConfig.leadOffset!, validatedConfig.leadUnits);
  const from = addToDate(tempFrom, validatedConfig.lagOffset! * -1, validatedConfig.lagUnits);

  return swapDates(from, tempTo);
};

export default useTimeState;
